import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "./AuthContext";

const SocialContext = createContext(null);

export function SocialProvider({ children }) {
  const { user } = useAuth();
  const [likes, setLikes] = useState(new Set());
  const [wishlist, setWishlist] = useState(new Set());

  const refresh = useCallback(async () => {
    if (!user || user.role !== "customer") {
      setLikes(new Set());
      setWishlist(new Set());
      return;
    }
    try {
      const [l, w] = await Promise.all([api.get("/me/likes"), api.get("/me/wishlist")]);
      setLikes(new Set((l.data.events || []).map((e) => e.id)));
      setWishlist(new Set((w.data.events || []).map((e) => e.id)));
    } catch {
      /* ignore */
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const toggleLike = useCallback(async (eventId) => {
    setLikes((s) => {
      const n = new Set(s);
      n.has(eventId) ? n.delete(eventId) : n.add(eventId);
      return n;
    });
    try {
      const { data } = await api.post(`/events/${eventId}/like`);
      setLikes((s) => {
        const n = new Set(s);
        data.liked ? n.add(eventId) : n.delete(eventId);
        return n;
      });
      return data.liked;
    } catch (e) {
      refresh();
      throw e;
    }
  }, [refresh]);

  const toggleWishlist = useCallback(async (eventId, occurrenceId = null) => {
    setWishlist((s) => {
      const n = new Set(s);
      n.has(eventId) ? n.delete(eventId) : n.add(eventId);
      return n;
    });
    try {
      const { data } = await api.post(`/events/${eventId}/wishlist`, { occurrence_id: occurrenceId });
      setWishlist((s) => {
        const n = new Set(s);
        data.wishlisted ? n.add(eventId) : n.delete(eventId);
        return n;
      });
      return data.wishlisted;
    } catch (e) {
      refresh();
      throw e;
    }
  }, [refresh]);

  return (
    <SocialContext.Provider value={{
      likes, wishlist,
      isLiked: (id) => likes.has(id),
      isWishlisted: (id) => wishlist.has(id),
      toggleLike, toggleWishlist, refresh,
    }}>
      {children}
    </SocialContext.Provider>
  );
}

export const useSocial = () => useContext(SocialContext);
